import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import ProtectedRoute from '../../../components/base/ProtectedRoute';
import type { DataItem } from '../../admin/components/UploadSection';
import DataCard from './DataCard';
import TasksList from './TasksList';
import NotificationPanel from './NotificationPanel';
import { getNotificationsForClient } from '../../../services/notifications';

interface Task {
  id: string;
  title: string;
  description: string;
  status: 'Pending' | 'In Progress' | 'Done' | 'Blocked';
  dueDate: string;
  createdAt: string;
  projectNumber: string;
  clientId: string;
}

export default function ProjectDetail() {
  const { projectNumber } = useParams<{ projectNumber: string }>();
  const navigate = useNavigate();
  const { user, getClients } = useAuth();
  const [projectData, setProjectData] = useState<{
    projectNumber: string;
    projectName: string;
    status: string;
    items: DataItem[];
    tasks: Task[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [clientId, setClientId] = useState<string>('');

  useEffect(() => {
    if (user && projectNumber) {
      const clients = getClients();
      const clientAccount = clients.find(c => c.username === user.username);
      const resolvedClientId = clientAccount?.id;

      if (!resolvedClientId) {
        setLoading(false);
        return;
      }

      setClientId(resolvedClientId);

      // Load data items for this project
      const stored = localStorage.getItem('admin_data_items');
      let projectItems: DataItem[] = [];
      let projectName = 'Unknown Project';
      let projectStatus = 'Active';

      if (stored) {
        const allItems: DataItem[] = JSON.parse(stored);
        projectItems = allItems.filter(
          item => item.clientId === resolvedClientId && 
                  item.projectNumber === projectNumber && 
                  !item.isPlaceholder
        );
        
        if (projectItems.length > 0) {
          projectName = projectItems[0].projectName || 'Unknown Project';
          projectStatus = (projectItems[0] as any).projectStatus || 'Active';
        }
      }

      // Load tasks for this project
      const storedTasks = localStorage.getItem('admin_project_tasks');
      let projectTasks: Task[] = [];

      if (storedTasks) {
        try {
          const allTasksMap: Record<string, Task[]> = JSON.parse(storedTasks);
          const taskKey = `${resolvedClientId}_${projectNumber}`;
          if (allTasksMap[taskKey]) {
            projectTasks = allTasksMap[taskKey].map(task => ({
              ...task,
              projectNumber,
              clientId: resolvedClientId
            }));
          }
        } catch {
          projectTasks = [];
        }
      }

      setProjectData({
        projectNumber,
        projectName,
        status: projectStatus,
        items: projectItems,
        tasks: projectTasks
      });
      setLoading(false);

      // Load unread notification count
      updateUnreadCount(resolvedClientId);
    }
  }, [user, projectNumber]);

  useEffect(() => {
    // Update unread count when panel closes
    if (!isNotificationPanelOpen && clientId) {
      updateUnreadCount(clientId);
    }
  }, [isNotificationPanelOpen, clientId]);

  const updateUnreadCount = (id: string) => {
    const notifications = getNotificationsForClient(id);
    const unread = notifications.filter(n => !n.read).length;
    setUnreadCount(unread);
  };

  const handleBack = () => {
    navigate('/portal');
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string; icon: string }> = {
      'Active': { bg: 'bg-green-50', text: 'text-green-700', icon: 'ri-play-circle-line' },
      'In Process': { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'ri-loader-4-line' },
      'Completed': { bg: 'bg-gray-100', text: 'text-gray-700', icon: 'ri-checkbox-circle-line' },
      'On Hold': { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'ri-pause-circle-line' }
    };
    const config = statusConfig[status] || statusConfig['Active'];
    return (
      <div className={`flex items-center gap-2 px-3 py-1 ${config.bg} ${config.text} text-sm font-medium rounded-full`}>
        <i className={`${config.icon} text-base`}></i>
        <span>{status}</span>
      </div>
    );
  };

  const getProgressBar = () => {
    if (!projectData || projectData.tasks.length === 0) return null;
    
    const doneTasks = projectData.tasks.filter(t => t.status === 'Done').length;
    const total = projectData.tasks.length;
    const percentage = Math.round((doneTasks / total) * 100);

    let barColor = 'bg-teal-500';
    if (percentage === 100) barColor = 'bg-green-500';
    else if (percentage >= 50) barColor = 'bg-teal-500';
    else if (percentage > 0) barColor = 'bg-amber-400';
    else barColor = 'bg-gray-300';

    return (
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
            <i className="ri-bar-chart-line text-sm"></i>
            Task Progress
          </span>
          <span className="text-xs font-semibold text-gray-700">
            {doneTasks} / {total} Done
            <span className="ml-1 text-gray-400">({percentage}%)</span>
          </span>
        </div>
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${barColor}`}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <ProtectedRoute requiredRole="client">
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-4"></i>
            <p className="text-sm text-gray-600">Loading project details...</p>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  if (!projectData) {
    return (
      <ProtectedRoute requiredRole="client">
        <div className="min-h-screen bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-base"></i>
              Back to Portal
            </button>
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <i className="ri-folder-unknow-line text-5xl text-gray-300 mb-4"></i>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Project Not Found</h3>
              <p className="text-sm text-gray-600">The project you're looking for doesn't exist or you don't have access to it.</p>
            </div>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute requiredRole="client">
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-base"></i>
              Back to Portal
            </button>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl">
                  <i className="ri-folder-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{projectData.projectNumber}</h1>
                  <p className="text-sm text-gray-600 mt-1">{projectData.projectName}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Notification Bell */}
                <button
                  onClick={() => setIsNotificationPanelOpen(true)}
                  className="relative w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-notification-3-line text-xl text-gray-700"></i>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {getStatusBadge(projectData.status)}
                <div className="flex items-center gap-2 px-3 py-1 bg-teal-50 text-teal-700 text-sm font-medium rounded-full">
                  <i className="ri-file-line text-base"></i>
                  <span>{projectData.items.length} {projectData.items.length === 1 ? 'document' : 'documents'}</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-full">
                  <i className="ri-task-line text-base"></i>
                  <span>{projectData.tasks.length} {projectData.tasks.length === 1 ? 'task' : 'tasks'}</span>
                </div>
              </div>
            </div>
            {getProgressBar()}
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-8">
            {/* Tasks Section */}
            {projectData.tasks.length > 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg">
                    <i className="ri-task-line text-xl text-blue-600"></i>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Project Tasks</h2>
                    <p className="text-sm text-gray-600">{projectData.tasks.length} {projectData.tasks.length === 1 ? 'task' : 'tasks'} assigned</p>
                  </div>
                </div>
                <TasksList tasks={projectData.tasks} />
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <i className="ri-task-line text-5xl text-gray-300 mb-4"></i>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Tasks Yet</h3>
                <p className="text-sm text-gray-600">No tasks have been assigned to this project.</p>
              </div>
            )}

            {/* Documents Section */}
            {projectData.items.length > 0 ? (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-file-list-3-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Project Documents</h2>
                    <p className="text-sm text-gray-600">{projectData.items.length} {projectData.items.length === 1 ? 'document' : 'documents'} available</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {projectData.items.map(item => (
                    <DataCard key={item.id} item={item} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <i className="ri-file-list-3-line text-5xl text-gray-300 mb-4"></i>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Documents Yet</h3>
                <p className="text-sm text-gray-600">No documents have been uploaded to this project.</p>
              </div>
            )}
          </div>
        </main>

        {/* Notification Panel */}
        <NotificationPanel
          clientId={clientId}
          isOpen={isNotificationPanelOpen}
          onClose={() => setIsNotificationPanelOpen(false)}
        />
      </div>
    </ProtectedRoute>
  );
}